import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LoginActivityAlert from "@/components/LoginActivityAlert";
import {
  PackageCheck, Inbox, Truck, Boxes, AlertCircle, Layers, Wrench, ClipboardList, Wallet, ScanBarcode, ArrowRightLeft, PackageOpen,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, tone = "text-primary", suffix = "" }) {
  return (
    <div className="surface-card p-5" data-testid={`whkpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="label-overline">{label}</span>
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-3">
        {value ?? "—"}
        {suffix && <span className="text-sm font-bold text-muted-foreground ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [k, setK] = useState(null);

  useEffect(() => {
    api.get("/warehouse/kpis").then(({ data }) => setK(data));
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" data-testid="warehouse-dashboard">
      <div>
        <div className="label-overline">Warehouse operations</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">
          {t("dashboards.warehouse.welcome")}
        </h1>
        <p className="text-muted-foreground mt-2">
          {t("dashboards.warehouse.subtitle")}
        </p>
      </div>

      <LoginActivityAlert />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="GRN pending" value={k?.grn_pending} icon={Inbox} tone="text-amber-600" />
        <Kpi label="GRN today" value={k?.grn_today} icon={PackageOpen} tone="text-emerald-600" />
        <Kpi label="Picks open" value={k?.picks_open} icon={ClipboardList} tone="text-violet-600" />
        <Kpi label="Packs today" value={k?.packs_today} icon={PackageCheck} />
        <Kpi label="Dispatches today" value={k?.dispatches_today} icon={Truck} tone="text-blue-600" />
        <Kpi label="Open transfers" value={k?.transfers_open} icon={ArrowRightLeft} tone="text-amber-700" />
        <Kpi label="Stock value" value={`₹${(k?.stock_value || 0).toLocaleString()}`} icon={Wallet} tone="text-emerald-700" />
        <Kpi label="Total qty on hand" value={(k?.total_qty || 0).toLocaleString()} icon={Boxes} />
        <Kpi label="SKUs tracked" value={k?.items_count} icon={Layers} />
        <Kpi label="Low-stock alerts" value={k?.low_stock_count} icon={AlertCircle} tone="text-red-600" />
        <Kpi label="Bins" value={k?.bins_count} icon={ScanBarcode} tone="text-violet-700" />
        <Kpi label="Equipment active" value={`${k?.equipment_active ?? 0}/${k?.equipment_total ?? 0}`} icon={Wrench} tone="text-blue-700" />
      </div>
    </div>
  );
}
